// src/components/Stripe/StripeWizard.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  CheckCircle, AlertCircle, RefreshCw, X,
  CreditCard, FileText, Building, Shield, ChevronRight
} from 'lucide-react';

const ONBOARDING_STEPS = [
  { 
    id: 'start', 
    title: 'Getting Started', 
    icon: CreditCard,
    description: 'Take Off account setup'
  },
  { 
    id: 'official business', 
    title: 'Business Info', 
    icon: Building,
    description: 'Tell us about your business'
  },
  { 
    id: 'identity', 
    title: 'Verify Identity', 
    icon: FileText,
    description: 'Confirm who you are'
  },
  { 
    id: 'payout', 
    title: 'Bank Details', 
    icon: Shield,
    description: 'Where to send your money'
  },
  { 
    id: 'complete', 
    title: 'Review', 
    icon: CheckCircle,
    description: 'Final verification'
  }
];

const WizardContainer = styled.div`
  background: rgba(0, 0, 0, 0.95);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  
  @media (max-width: 768px) {
    padding: 0.5rem;
    align-items: flex-start;
    overflow-y: auto;
  }
  
  @media (max-width: 480px) {
    padding: 0.25rem;
  }
`;

const WizardCard = styled.div`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border-radius: 16px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(99, 91, 255, 0.2);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    max-height: 95vh;
    max-width: 100%;
    border-radius: 12px;
    margin: 0.5rem;
  }
  
  @media (max-width: 480px) {
    max-height: 98vh;
    border-radius: 10px;
    margin: 0.25rem;
  }
`;

const WizardHeader = styled.div`
  padding: 2rem;
  background: rgba(99, 91, 255, 0.1);
  border-bottom: 1px solid rgba(99, 91, 255, 0.2);
  position: relative;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 1.25rem 1rem;
  }
  
  .close-btn {
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: transparent;
    border: none;
    color: rgba(255, 255, 255, 0.6);
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
      background: rgba(255, 255, 255, 0.1);
      color: white;
    }
    
    @media (max-width: 768px) {
      top: 0.75rem;
      right: 0.75rem;
      padding: 0.4rem;
    }
  }
  
  h2 {
    margin: 0 0 0.5rem 0;
    color: white;
    font-size: 1.5rem;
    
    @media (max-width: 768px) {
      font-size: 1.3rem;
      padding-right: 2rem;
    }
    
    @media (max-width: 480px) {
      font-size: 1.2rem;
    }
  }
  
  p {
    margin: 0;
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.9rem;
    
    @media (max-width: 768px) {
      font-size: 0.85rem;
      padding-right: 2rem;
    }
  }
`;

const ProgressBar = styled.div`
  padding: 2rem 2rem 1rem;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    padding: 1.5rem 1.5rem 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 1.25rem 1rem 0.75rem;
  }
  
  .steps-container {
    display: flex;
    justify-content: space-between;
    position: relative;
    margin-bottom: 1rem;
    
    &::before {
      content: '';
      position: absolute;
      top: 20px;
      left: 40px;
      right: 40px;
      height: 2px;
      background: rgba(255, 255, 255, 0.1);
      z-index: 1;
      
      @media (max-width: 768px) {
        left: 30px;
        right: 30px;
        top: 18px;
      }
      
      @media (max-width: 480px) {
        left: 25px;
        right: 25px;
        top: 16px;
      }
    }
  }
  
  .progress-line {
    position: absolute;
    top: 20px;
    left: 40px;
    height: 2px;
    background: linear-gradient(90deg, #635BFF, #00D4FF);
    z-index: 2;
    transition: width 0.5s ease;
    
    @media (max-width: 768px) {
      top: 18px;
      left: 30px;
    }
    
    @media (max-width: 480px) {
      top: 16px;
      left: 25px;
    }
  }
`;

const Step = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  position: relative;
  z-index: 3;
  flex: 1;
  
  .step-icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${props => {
      if (props.status === 'complete') return 'linear-gradient(135deg, #00D4FF, #635BFF)';
      if (props.status === 'current') return 'rgba(99, 91, 255, 0.3)';
      return 'rgba(255, 255, 255, 0.1)';
    }};
    border: 2px solid ${props => {
      if (props.status === 'complete') return '#00D4FF';
      if (props.status === 'current') return '#635BFF';
      return 'rgba(255, 255, 255, 0.2)';
    }};
    color: white;
    transition: all 0.3s ease;
    
    @media (max-width: 768px) {
      width: 36px;
      height: 36px;
    }
    
    @media (max-width: 480px) {
      width: 32px;
      height: 32px;
    }
  }
  
  .step-label {
    font-size: 0.75rem;
    color: ${props => props.status === 'complete' || props.status === 'current' ? 
      'white' : 'rgba(255, 255, 255, 0.5)'};
    text-align: center;
    font-weight: 500;
    
    @media (max-width: 768px) {
      font-size: 0.7rem;
    }
    
    @media (max-width: 480px) {
      font-size: 0.65rem;
    }
  }
`;

const WizardContent = styled.div`
  padding: 2rem;
  flex: 1;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 1.25rem 1rem;
  }
  
  .status-card {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 12px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
    border: 1px solid rgba(255, 255, 255, 0.1);
    
    @media (max-width: 768px) {
      padding: 1.25rem;
      margin-bottom: 1.25rem;
    }
    
    @media (max-width: 480px) {
      padding: 1rem;
      margin-bottom: 1rem;
    }
    
    &.error {
      background: rgba(244, 67, 54, 0.1);
      border-color: rgba(244, 67, 54, 0.3);
    }
    
    &.success {
      background: rgba(76, 175, 80, 0.1);
      border-color: rgba(76, 175, 80, 0.3);
    }
    
    &.warning {
      background: rgba(255, 193, 7, 0.1);
      border-color: rgba(255, 193, 7, 0.3);
    }
  }
  
  h3 {
    color: white;
    margin: 0 0 0.5rem 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 1.1rem;
    
    @media (max-width: 768px) {
      font-size: 1rem;
    }
    
    @media (max-width: 480px) {
      font-size: 0.95rem;
    }
  }
  
  p {
    color: rgba(255, 255, 255, 0.8);
    margin: 0 0 1rem 0;
    line-height: 1.6;
    font-size: 0.9rem;
    
    @media (max-width: 768px) {
      font-size: 0.85rem;
      line-height: 1.5;
    }
  }
  
  .requirements-list {
    list-style: none;
    padding: 0;
    margin: 1rem 0;
    
    @media (max-width: 768px) {
      margin: 0.75rem 0;
    }
    
    li {
      padding: 0.75rem;
      margin-bottom: 0.5rem;
      background: rgba(0, 0, 0, 0.2);
      border-radius: 8px;
      display: flex;
      align-items: center;
      gap: 0.75rem;
      color: rgba(255, 255, 255, 0.9);
      font-size: 0.9rem;
      
      @media (max-width: 768px) {
        padding: 0.6rem;
        font-size: 0.85rem;
        gap: 0.5rem;
      }
      
      &.complete {
        background: rgba(76, 175, 80, 0.1);
        border: 1px solid rgba(76, 175, 80, 0.3);
      }
      
      &.pending {
        background: rgba(255, 193, 7, 0.1);
        border: 1px solid rgba(255, 193, 7, 0.3);
      }
      
      .icon {
        flex-shrink: 0;
        
        @media (max-width: 768px) {
          width: 16px;
          height: 16px;
        }
      }
    }
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    margin-top: 1.25rem;
    gap: 0.5rem;
  }
  
  @media (max-width: 480px) {
    margin-top: 1rem;
    gap: 0.4rem;
  }
  
  button {
    flex: 1;
    padding: 0.875rem 1.5rem;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    font-size: 0.9rem;
    
    @media (max-width: 768px) {
      padding: 0.75rem 1.25rem;
      font-size: 0.85rem;
    }
    
    @media (max-width: 480px) {
      padding: 0.65rem 1rem;
      font-size: 0.8rem;
      gap: 0.4rem;
    }
    
    &.primary {
      background: linear-gradient(135deg, #635BFF, #00D4FF);
      color: white;
      border: none;
      
      &:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(99, 91, 255, 0.3);
      }
    }
    
    &.secondary {
      background: transparent;
      border: 1px solid rgba(255, 255, 255, 0.3);
      color: white;
      
      &:hover:not(:disabled) {
        background: rgba(255, 255, 255, 0.05);
      }
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none !important;
    }
  }
`;

const LoadingSpinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 0.8s linear infinite;
  
  @media (max-width: 768px) {
    width: 16px;
    height: 16px;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const StripeWizard = ({ 
  onClose, 
  onComplete, 
  userId, 
  userEmail, 
  shopName,
  existingAccountData = null // NEW PROP
}) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [accountStatus, setAccountStatus] = useState(existingAccountData?.status || null);
  const [loading, setLoading] = useState(false); // Start FALSE
  const [error, setError] = useState(null);
  const [accountId, setAccountId] = useState(existingAccountData?.accountId || null);
  const [requirements, setRequirements] = useState(existingAccountData?.currentlyDue || []);
  const [statusMessage, setStatusMessage] = useState(null);

  // REPLACE the checkExistingAccount useEffect with this:
  useEffect(() => {
    console.log('🎨 StripeWizard mounted with data:', existingAccountData);
    
    if (existingAccountData) {
      setAccountId(existingAccountData.accountId);
      setAccountStatus(existingAccountData.status);
      setRequirements(existingAccountData.currentlyDue || []);
      
      // Set step based on status
      if (existingAccountData.status === 'active') {
        setCurrentStepIndex(4);
      } else if (existingAccountData.status === 'pending_verification') {
        setCurrentStepIndex(4);
      } else if (existingAccountData.status === 'incomplete') {
        setCurrentStepIndex(1);
      }
      // else: stay at step 0 (no account = getting started)
    }
  }, [existingAccountData]);

  // REMOVE this entire useEffect:
  // useEffect(() => {
  //   if (!accountId && !accountStatus && !loading) {
  //     checkExistingAccount();
  //   }
  // }, [accountId, accountStatus, loading]);

  // Keep the other useEffects (body overflow, escape key)
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // DELETE the checkExistingAccount function entirely
  // const checkExistingAccount = async () => { ... }

  // UPDATE handleRefreshStatus to work properly:
  const handleRefreshStatus = async () => {
    if (loading) return;
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('🔄 Refreshing Stripe status...');
      
      const response = await fetch('/api/check-connect-account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId })
      });

      if (!response.ok) {
        throw new Error('Failed to check status');
      }

      const data = await response.json();
      console.log('📦 Status refresh result:', data);
      
      if (data.found) {
        setAccountId(data.accountId);
        setAccountStatus(data.status);
        setRequirements(data.currentlyDue || []);
        
        if (data.status === 'active') {
          setCurrentStepIndex(4);
          setStatusMessage({ 
            type: 'success', 
            text: '✅ Account is now active!' 
          });
          
          // Auto-close after 2 seconds
          setTimeout(() => {
            onComplete?.();
            onClose();
          }, 2000);
          
        } else if (data.status === 'pending_verification') {
          setCurrentStepIndex(4);
          setStatusMessage({ 
            type: 'warning', 
            text: '⏳ Still under review (1-2 business days)' 
          });
        } else if (data.status === 'incomplete') {
          setCurrentStepIndex(1);
          setStatusMessage({ 
            type: 'warning', 
            text: '⚠️ Please complete remaining steps' 
          });
        }
      } else {
        setStatusMessage({ 
          type: 'warning', 
          text: '⚠️ No account found. Click "Start Setup"' 
        });
      }
    } catch (err) {
      console.error('❌ Refresh error:', err);
      setError('Failed to refresh status: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

 const handleStartOnboarding = async () => {
  setLoading(true);
  setError(null);

  try {
    console.log('🚀 Starting Stripe onboarding for:', userId);
    
    // ⭐ ADD: Check if we're in live mode
    const isLiveMode = window.location.hostname === 'kalkode.com' || 
                       window.location.hostname === 'www.kalkode.com';
    
    if (isLiveMode) {
      // Show live mode warning
      setStatusMessage({
        type: 'warning',
        text: '⚠️ You are using LIVE mode. You will need to provide real identity verification information.'
      });
    }
    
    const response = await fetch('/api/create-connect-account', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId,
        email: userEmail,
        shopName: shopName || 'My Shop',
        existingAccountId: accountId
      })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `Failed to create account: ${response.status}`);
    }

    if (data.onboardingUrl) {
      console.log('✅ Got onboarding URL, redirecting to Stripe...');
      
      // ⭐ ADD: Store mode info
      localStorage.setItem('stripeOnboardingState', JSON.stringify({
        userId,
        accountId: data.accountId,
        mode: data.mode || 'live',
        startedAt: Date.now()
      }));
      
      // Redirect to Stripe
      window.location.href = data.onboardingUrl;
    } else {
      throw new Error('No onboarding URL received');
    }
  } catch (err) {
    console.error('❌ Onboarding error:', err);
    setError(err.message);
    setStatusMessage({
      type: 'error',
      text: err.message
    });
  } finally {
    setLoading(false);
  }
};


  const getStepStatus = (index) => {
    if (index < currentStepIndex) return 'complete';
    if (index === currentStepIndex) return 'current';
    return 'pending';
  };

  const progressPercentage = (currentStepIndex / (ONBOARDING_STEPS.length - 1)) * 100;

  const formatRequirement = (req) => {
    const formatMap = {
      'individual.id_number': 'Social Security Number',
      'individual.verification.document': 'Identity Document',
      'business.tax_id': 'Business Tax ID',
      'tos_acceptance.date': 'Terms of Service Acceptance',
      'external_account': 'Bank Account Information'
    };
    
    return formatMap[req] || req.replace(/_/g, ' ').replace(/\./g, ' - ');
  };

  const renderStepContent = () => {
    const step = ONBOARDING_STEPS[currentStepIndex];

    // Step 0: Getting Started
    if (currentStepIndex === 0) {
      return (
        <>
          <div className="status-card">
            <h3>
              <CreditCard size={20} />
              Welcome to Stripe Setup
            </h3>
            <p>
              You'll be redirected to Stripe's secure account setup process to verify and set up your payment details. 
              This will take about 5 minutes.
            </p>
            
            <h4 style={{ 
              color: 'white', 
              marginTop: '1.5rem', 
              marginBottom: '0.75rem',
              fontSize: '1rem'
            }}>
              What you'll need:
            </h4>
            <ul className="requirements-list">
              <li>
                <CheckCircle size={18} color="#4CAF50" className="icon" />
                Government-issued ID
              </li>
              <li>
                <CheckCircle size={18} color="#4CAF50" className="icon" />
                Bank account information
              </li>
              <li>
                <CheckCircle size={18} color="#4CAF50" className="icon" />
                Social Security Number
              </li>
              <li>
                <CheckCircle size={18} color="#4CAF50" className="icon" />
                Business details (if applicable)
              </li>
            </ul>
          </div>

          <ActionButtons>
            <button className="secondary" onClick={onClose}>
              Cancel
            </button>
            <button 
              className="primary" 
              onClick={handleStartOnboarding}
              disabled={loading}
            >
              {loading ? <LoadingSpinner /> : 'Start Setup'}
              <ChevronRight size={18} />
            </button>
          </ActionButtons>
        </>
      );
    }

    // Step 1: Business Info
    if (currentStepIndex === 1) {
      return (
        <>
          <div className="status-card">
            <h3>
              <Building size={20} />
              Business Information
            </h3>
            <p>
              Tell us about your business. This helps Stripe verify your account and ensure 
              you can receive payments securely.
            </p>
            
            <h4 style={{ 
              color: 'white', 
              marginTop: '1.5rem', 
              marginBottom: '0.75rem',
              fontSize: '1rem'
            }}>
              You'll be asked for:
            </h4>
            <ul className="requirements-list">
              <li>
                <CheckCircle size={18} color="#4CAF50" className="icon" />
                Business name and type
              </li>
              <li>
                <CheckCircle size={18} color="#4CAF50" className="icon" />
                Business address
              </li>
              <li>
                <CheckCircle size={18} color="#4CAF50" className="icon" />
                Tax identification number
              </li>
              <li>
                <CheckCircle size={18} color="#4CAF50" className="icon" />
                Business website (if applicable)
              </li>
            </ul>
          </div>

          <ActionButtons>
            <button className="secondary" onClick={onClose}>
              Cancel
            </button>
            <button 
              className="primary" 
              onClick={handleStartOnboarding}
              disabled={loading}
            >
              {loading ? <LoadingSpinner /> : 'Continue to Business Info'}
              <ChevronRight size={18} />
            </button>
          </ActionButtons>
        </>
      );
    }

    // Step 2: Verify Identity
    if (currentStepIndex === 2) {
      return (
        <>
          <div className="status-card">
            <h3>
              <FileText size={20} />
              Identity Verification
            </h3>
            <p>
              Stripe needs to verify your identity to comply with financial regulations. 
              This is a standard security procedure.
            </p>
            
            <h4 style={{ 
              color: 'white', 
              marginTop: '1.5rem', 
              marginBottom: '0.75rem',
              fontSize: '1rem'
            }}>
              Required documents:
            </h4>
            <ul className="requirements-list">
              <li>
                <CheckCircle size={18} color="#4CAF50" className="icon" />
                Government-issued photo ID
              </li>
              <li>
                <CheckCircle size={18} color="#4CAF50" className="icon" />
                Social Security Number (SSN)
              </li>
              <li>
                <CheckCircle size={18} color="#4CAF50" className="icon" />
                Date of birth
              </li>
              <li>
                <CheckCircle size={18} color="#4CAF50" className="icon" />
                Home address
              </li>
            </ul>
            
            <div style={{ 
              marginTop: '1rem', 
              padding: '1rem', 
              background: 'rgba(0, 0, 0, 0.3)', 
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <p style={{ fontSize: '0.85rem', margin: 0, color: 'rgba(255, 255, 255, 0.8)' }}>
                <strong>Note:</strong> This information is securely processed by Stripe and 
                is required by financial regulations. KalKode does not store your sensitive 
                personal documents.
              </p>
            </div>
          </div>

          <ActionButtons>
            <button className="secondary" onClick={onClose}>
              Cancel
            </button>
            <button 
              className="primary" 
              onClick={handleStartOnboarding}
              disabled={loading}
            >
              {loading ? <LoadingSpinner /> : 'Continue to Identity Verification'}
              <ChevronRight size={18} />
            </button>
          </ActionButtons>
        </>
      );
    }

    // Step 3: Bank Details
    if (currentStepIndex === 3) {
      return (
        <>
          <div className="status-card">
            <h3>
              <Shield size={20} />
              Bank Account & Payouts
            </h3>
            <p>
              Connect your bank account to receive payments. Stripe will deposit your earnings 
              directly to this account.
            </p>
            
            <h4 style={{ 
              color: 'white', 
              marginTop: '1.5rem', 
              marginBottom: '0.75rem',
              fontSize: '1rem'
            }}>
              You'll need:
            </h4>
            <ul className="requirements-list">
              <li>
                <CheckCircle size={18} color="#4CAF50" className="icon" />
                Bank account number
              </li>
              <li>
                <CheckCircle size={18} color="#4CAF50" className="icon" />
                Routing number
              </li>
              <li>
                <CheckCircle size={18} color="#4CAF50" className="icon" />
                Account holder name
              </li>
              <li>
                <CheckCircle size={18} color="#4CAF50" className="icon" />
                Account type (checking/savings)
              </li>
            </ul>
            
            <div style={{ 
              marginTop: '1rem', 
              padding: '1rem', 
              background: 'rgba(76, 175, 80, 0.1)', 
              borderRadius: '8px',
              border: '1px solid rgba(76, 175, 80, 0.3)'
            }}>
              <p style={{ fontSize: '0.85rem', margin: 0, color: 'rgba(255, 255, 255, 0.9)' }}>
                <strong>Secure:</strong> Your bank details are encrypted and stored securely 
                by Stripe. KalKode never has access to your full bank account information.
              </p>
            </div>
          </div>

          <ActionButtons>
            <button className="secondary" onClick={onClose}>
              Cancel
            </button>
            <button 
              className="primary" 
              onClick={handleStartOnboarding}
              disabled={loading}
            >
              {loading ? <LoadingSpinner /> : 'Continue to Bank Setup'}
              <ChevronRight size={18} />
            </button>
          </ActionButtons>
        </>
      );
    }

    // Step 4: Complete / Review
    if (currentStepIndex === 4) {
      if (accountStatus === 'active') {
        return (
          <>
            <div className="status-card success">
              <h3>
                <CheckCircle size={20} color="#4CAF50" />
                Account Active!
              </h3>
              <p>
                🎉 Your Stripe account is fully activated and ready to accept payments. 
                You can now sell items on KalKode!
              </p>
              
              <div style={{ 
                marginTop: '1rem', 
                padding: '1rem', 
                background: 'rgba(76, 175, 80, 0.2)', 
                borderRadius: '8px',
                border: '1px solid rgba(76, 175, 80, 0.4)'
              }}>
                <p style={{ fontSize: '0.9rem', margin: 0, color: 'rgba(255, 255, 255, 0.9)' }}>
                  <strong>Next steps:</strong> Start listing items in your shop and you'll be 
                  ready to accept payments immediately when customers make purchases.
                </p>
              </div>
            </div>

            <ActionButtons>
              <button className="primary" onClick={() => {
                onComplete?.();
                onClose();
              }}>
                <CheckCircle size={18} />
                Start Selling
              </button>
            </ActionButtons>
          </>
        );
      }

      if (accountStatus === 'pending_verification') {
        return (
          <>
            <div className="status-card warning">
              <h3>
                <AlertCircle size={20} color="#FFC107" />
                Verification in Progress
              </h3>
              <p>
                Stripe is reviewing your information. This usually takes 1-2 business days.
                We'll notify you once your account is approved.
              </p>
              
              {requirements.length > 0 && (
                <>
                  <h4 style={{ 
                    color: '#FFC107', 
                    marginTop: '1rem', 
                    marginBottom: '0.5rem',
                    fontSize: '1rem'
                  }}>
                    Additional Information Needed:
                  </h4>
                  <ul className="requirements-list">
                    {requirements.map((req, idx) => (
                      <li key={idx} className="pending">
                        <AlertCircle size={18} color="#FFC107" className="icon" />
                        {formatRequirement(req)}
                      </li>
                    ))}
                  </ul>
                </>
              )}
              
              <div style={{ 
                marginTop: '1rem', 
                padding: '1rem', 
                background: 'rgba(255, 193, 7, 0.1)', 
                borderRadius: '8px',
                border: '1px solid rgba(255, 193, 7, 0.3)'
              }}>
                <p style={{ fontSize: '0.85rem', margin: 0, color: 'rgba(255, 255, 255, 0.9)' }}>
                  <strong>What happens next:</strong> Stripe will review your application and 
                  may contact you if they need additional information. You'll receive an email 
                  once your account is fully activated.
                </p>
              </div>
            </div>

            <ActionButtons>
              <button className="secondary" onClick={onClose}>
                Close
              </button>
              <button className="primary" onClick={handleStartOnboarding}>
                Complete Requirements
                <ChevronRight size={18} />
              </button>
            </ActionButtons>
          </>
        );
      }

      // Incomplete setup
      if (accountStatus === 'incomplete') {
        return (
          <>
            <div className="status-card error">
              <h3>
                <AlertCircle size={20} color="#F44336" />
                Setup Incomplete
              </h3>
              <p>
                Your Stripe account setup wasn't finished. Please complete the remaining steps
                to start accepting payments.
              </p>
              
              {requirements.length > 0 && (
                <>
                  <h4 style={{ 
                    color: '#F44336', 
                    marginTop: '1rem', 
                    marginBottom: '0.5rem',
                    fontSize: '1rem'
                  }}>
                    Missing Information:
                  </h4>
                  <ul className="requirements-list">
                    {requirements.map((req, idx) => (
                      <li key={idx} className="pending">
                        <AlertCircle size={18} color="#F44336" className="icon" />
                        {formatRequirement(req)}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>

            <ActionButtons>
              <button className="secondary" onClick={onClose}>
                Cancel
              </button>
              <button 
                className="primary" 
                onClick={handleStartOnboarding}
                disabled={loading}
              >
                {loading ? <LoadingSpinner /> : 'Continue Setup'}
                <ChevronRight size={18} />
              </button>
            </ActionButtons>
          </>
        );
      }

      // No account status yet or unknown status
      return (
        <>
          <div className="status-card">
            <h3>
              <RefreshCw size={20} />
              Review Your Setup
            </h3>
            <p>
              Let's check your Stripe account status and make sure everything is set up correctly 
              for you to start accepting payments.
            </p>
            
            <div style={{ 
              marginTop: '1rem', 
              padding: '1rem', 
              background: 'rgba(0, 0, 0, 0.3)', 
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <p style={{ fontSize: '0.85rem', margin: 0, color: 'rgba(255, 255, 255, 0.8)' }}>
                <strong>Tip:</strong> If you've already started the setup process with Stripe, 
                click "Check Status" to see your current progress.
              </p>
            </div>
          </div>

          <ActionButtons>
            <button className="secondary" onClick={onClose}>
              Cancel
            </button>
            <button 
              className="primary" 
              onClick={handleRefreshStatus}
              disabled={loading}
            >
              {loading ? <LoadingSpinner /> : <RefreshCw size={18} />}
              Check Status
            </button>
          </ActionButtons>
        </>
      );
    }

    // Default fallback for steps 1-3 when user needs to complete Stripe onboarding
    return (
      <>
        <div className="status-card warning">
          <h3>
            <RefreshCw size={20} color="#FFC107" />
            Complete Setup on Stripe
          </h3>
          <p>
            You'll be redirected to Stripe to complete your account setup. 
            Once finished, return here to verify your account status.
          </p>
          
          <div style={{ 
            marginTop: '1rem', 
            padding: '1rem', 
            background: 'rgba(255, 193, 7, 0.1)', 
            borderRadius: '8px',
            border: '1px solid rgba(255, 193, 7, 0.3)'
          }}>
            <p style={{ fontSize: '0.85rem', margin: 0, color: 'rgba(255, 255, 255, 0.9)' }}>
              <strong>Remember:</strong> You'll need to complete the setup on Stripe's website 
              and then return here to finalize your account connection.
            </p>
          </div>
        </div>

        {statusMessage && (
          <div className={`status-card ${statusMessage.type}`}>
            <p>{statusMessage.text}</p>
          </div>
        )}

        <ActionButtons>
          <button className="secondary" onClick={onClose}>
            Cancel
          </button>
          <button 
            className="primary" 
            onClick={handleRefreshStatus}
            disabled={loading}
          >
            {loading ? <LoadingSpinner /> : <RefreshCw size={18} />}
            Check Status
          </button>
        </ActionButtons>
      </>
    );
  };

  return (
    <WizardContainer onClick={(e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    }}>
      <WizardCard>
        <WizardHeader>
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
          <h2>Payment Setup</h2>
          <p>Connect your Stripe account to accept payments on KalKode.</p>
        </WizardHeader>

        <ProgressBar>
          <div className="steps-container">
            <div className="progress-line" style={{ width: `${progressPercentage}%` }} />
            
            {ONBOARDING_STEPS.map((step, index) => {
              const StepIcon = step.icon;
              const status = getStepStatus(index);
              
              return (
                <Step key={step.id} status={status}>
                  <div className="step-icon">
                    {status === 'complete' ? (
                      <CheckCircle size={20} />
                    ) : (
                      <StepIcon size={20} />
                    )}
                  </div>
                  <div className="step-label">{step.title}</div>
                </Step>
              );
            })}
          </div>
        </ProgressBar>

        <WizardContent>
          {error && (
            <div className="status-card error">
              <h3>
                <AlertCircle size={20} color="#F44336" />
                Error
              </h3>
              <p>{error}</p>
            </div>
          )}
          
          {renderStepContent()}
        </WizardContent>
      </WizardCard>
    </WizardContainer>
  );
};

export default StripeWizard;